import { type Document, DocumentProviderError } from "../providers/types";
import { adaptAgentDemo } from "./agentDemoAdapter";
import type { AgentDemo } from "./agentDemoAdapter";
import { adaptQuality } from "./qualityAdapter";
import { type QualitySummary, type ViewNavigation } from "./types";
import { adaptViewNavigation } from "./viewNavigationAdapter";

/** Returns the current bearer token (Cognito id token), or `undefined`. */
export type TokenProvider = () => string | undefined;

/**
 * The Cloud Knowledge Workspace data contract. Both the authenticated console
 * (API-backed) and the public preview (static-backed) implement this so the
 * UI is ignorant of where Knowledge Views come from.
 */
export interface KnowledgeProvider {
  readonly id: string;
  /** Load the view-aware navigation (the primary IA). */
  loadViewNavigation(signal?: AbortSignal): Promise<ViewNavigation>;
  /** Load the narrated Markdown for one view (by view key). */
  loadViewDocument(viewKey: string, signal?: AbortSignal): Promise<Document>;
  /**
   * Load any document the navigation advertises, by slug.
   *
   * A slug is a view key, a `<view>/<section>` deep-link (nav schema v2 writes
   * one standalone file per section), or a related page's key. Reading the
   * section the producer wrote is what keeps the reader honest: reconstructing
   * a section by splitting the composed README on its headings depends on the
   * prose keeping a heading shape the pipeline never promised, and silently
   * shows the wrong body — or nothing — when it does not.
   */
  loadDocumentSlug(slug: string, signal?: AbortSignal): Promise<Document>;
  /** Load the Knowledge Quality (KQI) summary, or `null` when unavailable. */
  loadQuality(signal?: AbortSignal): Promise<QualitySummary | null>;
  /**
   * Load a recorded CDX Agent demo fixture, or `null` when unavailable.
   * Optional — only the static preview bundle ships `agent-demo.json`.
   */
  loadAgentDemo?(signal?: AbortSignal): Promise<AgentDemo | null>;
  /**
   * Base URL for resolving a view document's **relative** assets (the embedded
   * `./diagrams/<name>.png` images), or `undefined` when the provider does not
   * serve assets as static URLs (e.g. the API provider). The viewer passes this
   * to the Markdown renderer so relative image links resolve correctly.
   */
  viewAssetBaseUrl?(viewKey: string): string | undefined;
}

// -- API-backed (authenticated console) ------------------------------------

export interface ApiKnowledgeProviderOptions {
  readonly baseUrl: string;
  readonly runId: string;
  readonly getToken?: TokenProvider;
}

/**
 * Knowledge provider backed by the control-plane API (Stage 5/7):
 *
 *  - `loadViewNavigation` → `GET /v1/runs/{id}/views`
 *  - `loadViewDocument`   → `GET /v1/runs/{id}/documents/{view}`
 *  - `loadQuality`        → `GET /v1/runs/{id}/quality` (404 → null)
 */
export class ApiKnowledgeProvider implements KnowledgeProvider {
  readonly id = "api-knowledge";

  private readonly baseUrl: string;
  private readonly runId: string;
  private readonly getToken: TokenProvider;
  private navPromise: Promise<ViewNavigation> | undefined;
  private qualityPromise: Promise<QualitySummary | null> | undefined;
  private readonly docCache = new Map<string, Promise<Document>>();

  constructor(options: ApiKnowledgeProviderOptions) {
    this.baseUrl = options.baseUrl.replace(/\/+$/, "");
    this.runId = options.runId;
    this.getToken = options.getToken ?? (() => undefined);
  }

  loadViewNavigation(signal?: AbortSignal): Promise<ViewNavigation> {
    if (!this.navPromise) {
      this.navPromise = this.fetchNav().catch((err) => {
        this.navPromise = undefined;
        throw err;
      });
    }
    return raceSignal(this.navPromise, signal);
  }

  loadViewDocument(viewKey: string, signal?: AbortSignal): Promise<Document> {
    return this.loadDocumentSlug(viewKey, signal);
  }

  loadDocumentSlug(slug: string, signal?: AbortSignal): Promise<Document> {
    const cached = this.docCache.get(slug);
    if (cached) return raceSignal(cached, signal);
    const promise = this.fetchDocument(slug).catch((err) => {
      this.docCache.delete(slug);
      throw err;
    });
    this.docCache.set(slug, promise);
    return raceSignal(promise, signal);
  }

  loadQuality(signal?: AbortSignal): Promise<QualitySummary | null> {
    if (!this.qualityPromise) {
      this.qualityPromise = this.fetchQuality().catch((err) => {
        this.qualityPromise = undefined;
        throw err;
      });
    }
    return raceSignal(this.qualityPromise, signal);
  }

  private async fetchNav(): Promise<ViewNavigation> {
    const url = `${this.baseUrl}/v1/runs/${encodeURIComponent(this.runId)}/views`;
    const json = await this.fetchJson(url);
    const nav = pick(json, "views");
    return adaptViewNavigation(nav);
  }

  private async fetchDocument(slug: string): Promise<Document> {
    // Each slug segment is encoded separately: the `/` in `<view>/<section>` is
    // a path separator the router splits on, so percent-encoding it would send
    // one opaque segment the backend cannot resolve.
    const path = slug.split("/").map(encodeURIComponent).join("/");
    const url = `${this.baseUrl}/v1/runs/${encodeURIComponent(this.runId)}/documents/${path}`;
    const json = await this.fetchJson(url);
    const document = isRecord(json) ? (json.document as unknown) : undefined;
    if (!isRecord(document) || typeof document.markdown !== "string") {
      throw new DocumentProviderError({
        code: "invalid_document",
        message: `Document response from ${url} is missing 'document.markdown'`,
      });
    }
    const title = typeof document.title === "string" ? document.title : slug;
    return {
      id: slug,
      label: title,
      markdown: document.markdown,
      metadata: { title },
    };
  }

  private async fetchQuality(): Promise<QualitySummary | null> {
    const url = `${this.baseUrl}/v1/runs/${encodeURIComponent(this.runId)}/quality`;
    try {
      const json = await this.fetchJson(url);
      return adaptQuality(pick(json, "quality"));
    } catch (err) {
      if (err instanceof DocumentProviderError && err.code === "not_found")
        return null;
      throw err;
    }
  }

  private async fetchJson(url: string): Promise<unknown> {
    const headers: Record<string, string> = { accept: "application/json" };
    const token = this.getToken();
    if (token) headers.authorization = `Bearer ${token}`;
    return fetchJsonShared(url, headers);
  }
}

// -- static-backed (public preview / offline) ------------------------------

export interface StaticKnowledgeProviderOptions {
  /** Base URL that hosts `views/navigation.json`, `views/<key>/README.md`, `quality.json`. */
  readonly baseUrl: string;
}

/**
 * Knowledge provider that reads a pre-rendered, sanitized view bundle from a
 * static origin (bundled demo content or a public CloudFront prefix). No auth.
 */
export class StaticKnowledgeProvider implements KnowledgeProvider {
  readonly id = "static-knowledge";

  private readonly baseUrl: string;
  private navPromise: Promise<ViewNavigation> | undefined;
  private qualityPromise: Promise<QualitySummary | null> | undefined;
  private agentDemoPromise: Promise<AgentDemo | null> | undefined;
  private readonly docCache = new Map<string, Promise<Document>>();

  constructor(options: StaticKnowledgeProviderOptions) {
    this.baseUrl = options.baseUrl.replace(/\/+$/, "");
  }

  loadViewNavigation(signal?: AbortSignal): Promise<ViewNavigation> {
    if (!this.navPromise) {
      this.navPromise = this.fetchNav().catch((err) => {
        this.navPromise = undefined;
        throw err;
      });
    }
    return raceSignal(this.navPromise, signal);
  }

  loadViewDocument(viewKey: string, signal?: AbortSignal): Promise<Document> {
    return this.loadDocumentSlug(viewKey, signal);
  }

  loadDocumentSlug(slug: string, signal?: AbortSignal): Promise<Document> {
    const cached = this.docCache.get(slug);
    if (cached) return raceSignal(cached, signal);
    const promise = this.fetchDocument(slug).catch((err) => {
      this.docCache.delete(slug);
      throw err;
    });
    this.docCache.set(slug, promise);
    return raceSignal(promise, signal);
  }

  loadQuality(signal?: AbortSignal): Promise<QualitySummary | null> {
    if (!this.qualityPromise) {
      this.qualityPromise = this.fetchQuality().catch((err) => {
        this.qualityPromise = undefined;
        throw err;
      });
    }
    return raceSignal(this.qualityPromise, signal);
  }

  loadAgentDemo(signal?: AbortSignal): Promise<AgentDemo | null> {
    if (!this.agentDemoPromise) {
      this.agentDemoPromise = this.fetchAgentDemo().catch((err) => {
        this.agentDemoPromise = undefined;
        throw err;
      });
    }
    return raceSignal(this.agentDemoPromise, signal);
  }

  viewAssetBaseUrl(viewKey: string): string {
    // A view is published as a folder (`views/<view>/README.md`) with a sibling
    // `diagrams/` directory, so its assets resolve under `views/<view>/`.
    return `${this.baseUrl}/views/${encodeURIComponent(viewKey)}`;
  }

  private async fetchNav(): Promise<ViewNavigation> {
    const json = await fetchJsonShared(
      `${this.baseUrl}/views/navigation.json`,
      {
        accept: "application/json",
      },
    );
    // Static bundles may store the nav directly or under a "views" envelope.
    const nav =
      isRecord(json) && "views" in json && isRecord(json.views)
        ? json.views
        : json;
    return adaptViewNavigation(nav);
  }

  private async fetchDocument(slug: string): Promise<Document> {
    const relpath = await this.resolveRelpath(slug);
    const url = `${this.baseUrl}/${relpath}`;
    const markdown = await fetchTextShared(url);
    const title = markdown.match(/^#\s+(.+)$/m)?.[1]?.trim() ?? slug;
    return { id: slug, label: title, markdown, metadata: { title } };
  }

  /**
   * Resolve a slug against the published manifest: a view key to its composed
   * document, a `<view>/<section>` deep-link to that section's standalone file,
   * a related page's key to its path.
   *
   * Only the view-key case has a convention to fall back on (older single-file
   * bundles). A section or related page is resolvable solely because the run
   * published it, so an unresolvable slug is a genuine 404 rather than a guessed
   * URL that fails one request later with a less useful message.
   */
  private async resolveRelpath(slug: string): Promise<string> {
    const legacy = `views/${encodeURIComponent(slug)}.md`;
    let nav: ViewNavigation;
    try {
      nav = await this.loadViewNavigation();
    } catch {
      return legacy;
    }
    const view = nav.views.find((v) => v.key === slug);
    if (view) return view.outputRelpath ?? legacy;

    if (slug.includes("/")) {
      const [viewKey, sectionKey] = splitOnce(slug, "/");
      const section = nav.views
        .find((v) => v.key === viewKey)
        ?.sections.find((s) => s.key === sectionKey);
      if (section?.path) return section.path;
    }

    const page = nav.relatedPages.find((p) => p.key === slug);
    if (page) return page.path;

    throw new DocumentProviderError({
      code: "not_found",
      message: `Document '${slug}' is not in this bundle's navigation.`,
    });
  }

  private async fetchQuality(): Promise<QualitySummary | null> {
    try {
      const json = await fetchJsonShared(`${this.baseUrl}/quality.json`, {
        accept: "application/json",
      });
      return adaptQuality(json);
    } catch (err) {
      if (err instanceof DocumentProviderError && err.code === "not_found")
        return null;
      throw err;
    }
  }

  private async fetchAgentDemo(): Promise<AgentDemo | null> {
    try {
      const json = await fetchJsonShared(`${this.baseUrl}/agent-demo.json`, {
        accept: "application/json",
      });
      return adaptAgentDemo(json);
    } catch (err) {
      if (err instanceof DocumentProviderError && err.code === "not_found")
        return null;
      throw err;
    }
  }
}

// -- shared transport ------------------------------------------------------

async function fetchResponse(
  url: string,
  headers: Record<string, string>,
): Promise<Response> {
  let response: Response;
  try {
    response = await fetch(url, { headers, cache: "no-cache" });
  } catch (cause) {
    throw new DocumentProviderError({
      code: "network_error",
      message: `Failed to fetch ${url}`,
      cause,
    });
  }
  if (response.status === 401 || response.status === 403) {
    throw new DocumentProviderError({
      code: "unauthorized",
      message: `Access denied for ${url} (HTTP ${response.status})`,
    });
  }
  if (response.status === 404) {
    throw new DocumentProviderError({
      code: "not_found",
      message: `Not found: ${url}`,
    });
  }
  if (!response.ok) {
    throw new DocumentProviderError({
      code: "network_error",
      message: `Unexpected HTTP ${response.status} for ${url}`,
    });
  }
  return response;
}

async function fetchJsonShared(
  url: string,
  headers: Record<string, string>,
): Promise<unknown> {
  const response = await fetchResponse(url, headers);
  return response.json().catch((cause) => {
    throw new DocumentProviderError({
      code: "invalid_document",
      message: `Response from ${url} is not valid JSON`,
      cause,
    });
  });
}

async function fetchTextShared(url: string): Promise<string> {
  const response = await fetchResponse(url, {
    accept: "text/markdown, text/plain",
  });
  return response.text();
}

function splitOnce(value: string, separator: string): [string, string] {
  const at = value.indexOf(separator);
  return at < 0
    ? [value, ""]
    : [value.slice(0, at), value.slice(at + separator.length)];
}

function pick(json: unknown, key: string): unknown {
  return isRecord(json) && key in json ? json[key] : json;
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}

function raceSignal<T>(promise: Promise<T>, signal?: AbortSignal): Promise<T> {
  if (!signal) return promise;
  if (signal.aborted) {
    return Promise.reject(
      signal.reason instanceof Error
        ? signal.reason
        : new DOMException("signal is aborted without reason", "AbortError"),
    );
  }
  return new Promise<T>((resolve, reject) => {
    const onAbort = () => {
      reject(
        signal.reason instanceof Error
          ? signal.reason
          : new DOMException("signal is aborted without reason", "AbortError"),
      );
    };
    signal.addEventListener("abort", onAbort, { once: true });
    promise.then(
      (value) => {
        signal.removeEventListener("abort", onAbort);
        resolve(value);
      },
      (err: unknown) => {
        signal.removeEventListener("abort", onAbort);
        reject(err);
      },
    );
  });
}
